// Tema de colores para Servisoft Algodón
// Basado en la estética del logo de Servisoft

export const theme = {
  colors: {
    // Colores principales del logo
    primary: '#2D5C92',        // Azul principal
    primaryDark: '#0A2742',     // Azul más cercano al nuevo logo para fondos
    primaryVeryDark: '#082036', // Azul profundo para headers
    secondary: '#98BF06',       // Verde lima para acentos (del logo)
    secondaryBright: '#9ed022', // Verde brillante usado en ítems activos del menú

    // Colores de fondo
    background: '#ffffff',
    backgroundGray: '#f5f5f5',

    // Colores de texto
    textPrimary: '#023059',
    textSecondary: '#374151',
    textLight: '#ffffff',       // Blanco puro para contraste máximo
    textMuted: '#6b7280',

    // Colores de estado (mejorados - basados en paleta verde/azul)
    success: '#7CB342',         // Verde natural (más cercano al logo)
    successLight: '#AED581',    // Verde claro para fondos
    successDark: '#558B2F',     // Verde oscuro para hover

    warning: '#FFA726',         // Naranja cálido (más suave)
    warningLight: '#FFE0B2',    // Naranja claro para fondos
    warningDark: '#B45309',     // Naranja oscuro para texto sobre warningLight

    error: '#E53935',           // Rojo (mantener para claridad)
    errorLight: '#FFCDD2',      // Rojo claro para fondos
    errorDark: '#C62828',       // Rojo oscuro para texto sobre errorLight

    info: '#42A5F5',            // Azul info (más cercano al azul del logo)
    infoLight: '#BBDEFB',       // Azul claro para fondos
    infoDark: '#1976D2',        // Azul oscuro

    // Colores específicos para módulos
    purchases: '#7CB342',       // Verde para compras/ingresos
    sales: '#42A5F5',           // Azul para ventas/salidas
    stock: '#2D5C92',           // Azul principal para stock
    pending: '#FFA726',         // Naranja suave para pendientes

    // Colores de bordes
    border: '#d9d9d9',
    borderLight: '#f0f0f0',

    // Sidebar
    sidebarBg: '#051529',
    sidebarGradientFrom: '#071c34',
    sidebarGradientTo: '#0f2b4c',
    sidebarActiveBg: '#9ed022',
    sidebarActiveText: '#0A2742',
    sidebarText: 'rgba(255,255,255,0.85)',
    sidebarTextMuted: 'rgba(255,255,255,0.65)',
    sidebarBorder: '#123456',
  },

  // Estilos comunes
  // Más redondeado y con sombra más suave — ver docs/plan_unificacion_visual.html
  // sección "Ficha de referencia" (estilo de tabla actualizado).
  styles: {
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    boxShadowLight: '0 2px 8px rgba(0,0,0,0.1)',
  },

  // Sombras
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },

  // Border Radius
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '16px',
    xl: '20px',
    full: '9999px',
  },

  // Estilos para contenedores
  containerStyles: {
    light: {
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      boxShadow: '0 0 10px 0 rgba(0,0,0,0.2)',
      border: '1px solid #e5e7eb',
      padding: '20px',
    },
    header: {
      backgroundColor: '#023059',
      color: '#ffffff',
      padding: '16px 20px',
      borderRadius: '8px 8px 0 0',
      fontSize: '18px',
      fontWeight: 600,
    }
  },

  // Fuentes
  fonts: {
    primary: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    code: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
  },

  // Tamaños de fuente
  fontSizes: {
    small: '12px',
    normal: '14px',
    medium: '16px',
    large: '18px',
    xlarge: '24px',
    xxlarge: '32px',
  },

  // Paleta categórica única para gráficos (series 1 a 6, en este orden).
  // Reemplaza los colores sueltos por archivo (chart.js demo, hash por nombre, grises ad-hoc).
  // Ver docs/plan_unificacion_visual.html — sección "Gráficas" y "Ficha de referencia".
  chartPalette: [
    '#2D5C92', // azul marca
    '#98BF06', // verde lima
    '#42A5F5', // celeste info
    '#FFA726', // naranja alerta
    '#7CB342', // verde éxito
    '#9AA5B1', // gris neutro (overflow / "otros")
  ],
};

// Estilos comunes reutilizables
export const commonStyles = {
  card: {
    backgroundColor: '#ffffff',
    borderRadius: theme.styles.borderRadius,
    boxShadow: theme.styles.boxShadow,
    padding: '20px',
  },

  cardHeader: {
    backgroundColor: theme.colors.primaryDark,
    color: theme.colors.textLight,
    padding: '16px 20px',
    borderTopLeftRadius: theme.styles.borderRadius,
    borderTopRightRadius: theme.styles.borderRadius,
    fontSize: theme.fontSizes.large,
    fontWeight: 600,
  },

  button: {
    primary: {
      backgroundColor: theme.colors.primary,
      color: theme.colors.textLight,
      border: 'none',
      borderRadius: theme.styles.borderRadius,
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: theme.fontSizes.normal,
      fontWeight: 500,
      transition: 'all 0.3s ease',
    },

    secondary: {
      backgroundColor: theme.colors.secondary,
      color: theme.colors.textLight,
      border: 'none',
      borderRadius: theme.styles.borderRadius,
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: theme.fontSizes.normal,
      fontWeight: 500,
      transition: 'all 0.3s ease',
    },
  },
};

